import { useEffect, useMemo, useState } from 'react';
import { Eye, Phone, MessageCircle, Navigation2, Search as SearchIcon, TrendingUp, MapPin, Tag, Layers } from 'lucide-react';
import { Card, SectionHeader, Badge } from '@/components/ui';
import { supabase } from '@/lib/supabase';
import { formatNumber, timeAgo } from '@/lib/format';
import { Listing, SearchQuery } from '@/lib/types';

export default function Analytics() {
  const [listings, setListings] = useState<Listing[]>([]);
  const [queries, setQueries] = useState<SearchQuery[]>([]);

  useEffect(() => {
    void (async () => {
      const [l, q] = await Promise.all([
        supabase.from('listings').select('*').limit(500),
        supabase.from('search_queries').select('*').order('created_at', { ascending: false }).limit(200),
      ]);
      setListings((l.data as Listing[]) ?? []);
      setQueries((q.data as SearchQuery[]) ?? []);
    })();
  }, []);

  const totals = useMemo(() => {
    return listings.reduce(
      (a, l) => ({
        views: a.views + l.view_count,
        calls: a.calls + l.call_clicks,
        whatsapp: a.whatsapp + l.whatsapp_clicks,
        directions: a.directions + l.direction_clicks,
        enquiries: a.enquiries + l.enquiries,
      }),
      { views: 0, calls: 0, whatsapp: 0, directions: 0, enquiries: 0 },
    );
  }, [listings]);

  const top = (metric: keyof Listing, n = 6) =>
    [...listings].sort((a, b) => Number(b[metric]) - Number(a[metric])).slice(0, n);

  const grouped = (field: 'city' | 'category') => {
    const map = new Map<string, number>();
    listings.forEach((l) => {
      const k = l[field];
      if (!k) return;
      map.set(k, (map.get(k) ?? 0) + l.view_count);
    });
    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8);
  };

  const searchCategoryCounts = useMemo(() => {
    const m = new Map<string, number>();
    queries.forEach((q) => q.category && m.set(q.category, (m.get(q.category) ?? 0) + 1));
    return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [queries]);

  const searchCityCounts = useMemo(() => {
    const m = new Map<string, number>();
    queries.forEach((q) => q.city && m.set(q.city, (m.get(q.city) ?? 0) + 1));
    return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [queries]);

  const noResults = queries.filter((q) => q.result_count === 0);

  const combos = useMemo(() => {
    const m = new Map<string, { query: string; city: string | null; category: string | null; count: number; total_results: number; zero: number }>();
    queries.forEach((q) => {
      const key = `${q.query.toLowerCase()}||${q.city ?? ''}||${q.category ?? ''}`;
      const prev = m.get(key);
      if (prev) {
        prev.count += 1;
        prev.total_results += q.result_count;
        if (q.result_count === 0) prev.zero += 1;
      } else {
        m.set(key, { query: q.query, city: q.city, category: q.category, count: 1, total_results: q.result_count, zero: q.result_count === 0 ? 1 : 0 });
      }
    });
    return [...m.values()].sort((a, b) => b.count - a.count).slice(0, 20);
  }, [queries]);

  const queryCityCombos = useMemo(() => {
    const m = new Map<string, { query: string; city: string; count: number; avg: number }>();
    queries.forEach((q) => {
      if (!q.city || !q.query) return;
      const key = `${q.query.toLowerCase()}||${q.city}`;
      const prev = m.get(key);
      if (prev) { prev.count += 1; prev.avg = (prev.avg * (prev.count - 1) + q.result_count) / prev.count; }
      else m.set(key, { query: q.query, city: q.city, count: 1, avg: q.result_count });
    });
    return [...m.values()].sort((a, b) => b.count - a.count).slice(0, 10);
  }, [queries]);

  const queryCategoryCombos = useMemo(() => {
    const m = new Map<string, { query: string; category: string; count: number; avg: number }>();
    queries.forEach((q) => {
      if (!q.category || !q.query) return;
      const key = `${q.query.toLowerCase()}||${q.category}`;
      const prev = m.get(key);
      if (prev) { prev.count += 1; prev.avg = (prev.avg * (prev.count - 1) + q.result_count) / prev.count; }
      else m.set(key, { query: q.query, category: q.category, count: 1, avg: q.result_count });
    });
    return [...m.values()].sort((a, b) => b.count - a.count).slice(0, 10);
  }, [queries]);

  const cityCategoryCombos = useMemo(() => {
    const m = new Map<string, { city: string; category: string; count: number; avg: number }>();
    queries.forEach((q) => {
      if (!q.city || !q.category) return;
      const key = `${q.city}||${q.category}`;
      const prev = m.get(key);
      if (prev) { prev.count += 1; prev.avg = (prev.avg * (prev.count - 1) + q.result_count) / prev.count; }
      else m.set(key, { city: q.city, category: q.category, count: 1, avg: q.result_count });
    });
    return [...m.values()].sort((a, b) => b.count - a.count).slice(0, 10);
  }, [queries]);

  return (
    <div className="space-y-6 text-slate-800">
      <SectionHeader
        title="Analytics"
        subtitle="Auto-generated dashboards tracking every user interaction across the directory. Popular categories, unmet searches, growing areas — all in real time."
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <MetricCard icon={Eye} label="Total views" value={totals.views} tone="text-cyan-600" />
        <MetricCard icon={Phone} label="Call clicks" value={totals.calls} tone="text-emerald-600" />
        <MetricCard icon={MessageCircle} label="WhatsApp" value={totals.whatsapp} tone="text-emerald-600" />
        <MetricCard icon={Navigation2} label="Directions" value={totals.directions} tone="text-amber-600" />
        <MetricCard icon={TrendingUp} label="Enquiries" value={totals.enquiries} tone="text-emerald-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <RankedList
          title="Most viewed businesses"
          icon={<Eye className="h-4 w-4 text-cyan-600" />}
          rows={top('view_count').map((l) => ({ label: l.name, sub: `${l.city} · ${l.category}`, value: formatNumber(l.view_count) }))}
        />
        <RankedList
          title="Highest call clicks"
          icon={<Phone className="h-4 w-4 text-emerald-600" />}
          rows={top('call_clicks').map((l) => ({ label: l.name, sub: `${l.city} · ${l.category}`, value: formatNumber(l.call_clicks) }))}
        />
        <RankedList
          title="Highest WhatsApp clicks"
          icon={<MessageCircle className="h-4 w-4 text-emerald-600" />}
          rows={top('whatsapp_clicks').map((l) => ({ label: l.name, sub: `${l.city} · ${l.category}`, value: formatNumber(l.whatsapp_clicks) }))}
        />
        <RankedList
          title="Highest direction clicks"
          icon={<Navigation2 className="h-4 w-4 text-amber-600" />}
          rows={top('direction_clicks').map((l) => ({ label: l.name, sub: `${l.city} · ${l.category}`, value: formatNumber(l.direction_clicks) }))}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <BarList
          title="Popular categories (by views)"
          icon={<Tag className="h-4 w-4 text-emerald-600" />}
          rows={grouped('category')}
        />
        <BarList
          title="Popular areas (by views)"
          icon={<MapPin className="h-4 w-4 text-emerald-600" />}
          rows={grouped('city')}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RankedList
          title="Most searched categories"
          icon={<SearchIcon className="h-4 w-4 text-cyan-600" />}
          rows={searchCategoryCounts.map(([k, v]) => ({ label: k, sub: 'search queries', value: v.toString() }))}
        />
        <RankedList
          title="Most searched cities"
          icon={<SearchIcon className="h-4 w-4 text-cyan-600" />}
          rows={searchCityCounts.map(([k, v]) => ({ label: k, sub: 'search queries', value: v.toString() }))}
        />
      </div>

      <Card className="p-5 mt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Layers className="h-4 w-4 text-emerald-600" /> Search combinations
            </div>
            <div className="text-xs text-slate-500 font-medium mt-1">
              Every unique (query + city + category) tuple your visitors search for, ranked by frequency.
            </div>
          </div>
          <Badge tone="info">{combos.length} tracked</Badge>
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead className="text-slate-500 font-bold text-[11px] uppercase tracking-wider">
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 pr-3">Query</th>
                <th className="text-left py-2 pr-3">City</th>
                <th className="text-left py-2 pr-3">Category</th>
                <th className="text-right py-2 pr-3">Searches</th>
                <th className="text-right py-2 pr-3">Avg results</th>
                <th className="text-right py-2">Zero-result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-semibold">
              {combos.map((c, i) => {
                const avg = c.count > 0 ? c.total_results / c.count : 0;
                return (
                  <tr key={i} className="hover:bg-slate-50 transition">
                    <td className="py-2.5 pr-3 text-slate-900 font-bold">"{c.query}"</td>
                    <td className="py-2.5 pr-3 text-slate-600">{c.city ?? <span className="text-slate-400">any</span>}</td>
                    <td className="py-2.5 pr-3 text-slate-600">{c.category ?? <span className="text-slate-400">any</span>}</td>
                    <td className="py-2.5 pr-3 text-right font-mono text-emerald-700 font-extrabold tabular-nums">{c.count}</td>
                    <td className="py-2.5 pr-3 text-right font-mono text-slate-700 tabular-nums">{avg.toFixed(1)}</td>
                    <td className="py-2.5 text-right">
                      {c.zero > 0 ? (
                        <span className="inline-block text-[11px] font-mono font-bold px-2 py-0.5 rounded bg-red-100 text-red-700 border border-red-200">{c.zero}</span>
                      ) : (
                        <span className="text-slate-400 text-[11px]">0</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {combos.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-slate-400">No combination data yet — run a search on the public site.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <ComboCard title="Query × City" icon={<MapPin className="h-4 w-4 text-emerald-600" />} rows={queryCityCombos.map((r) => ({ a: r.query, b: r.city, count: r.count, avg: r.avg }))} />
        <ComboCard title="Query × Category" icon={<Tag className="h-4 w-4 text-emerald-600" />} rows={queryCategoryCombos.map((r) => ({ a: r.query, b: r.category, count: r.count, avg: r.avg }))} />
        <ComboCard title="City × Category" icon={<Layers className="h-4 w-4 text-emerald-600" />} rows={cityCategoryCombos.map((r) => ({ a: r.city, b: r.category, count: r.count, avg: r.avg }))} />
      </div>

      <Card className="p-5 mt-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <SearchIcon className="h-4 w-4 text-red-600" /> Searches with no results
          </div>
          <Badge tone="warn">{noResults.length} unmet</Badge>
        </div>
        {noResults.length === 0 ? (
          <div className="text-xs text-slate-500 py-4 font-semibold">Every search returned matches — nice coverage.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {noResults.map((q) => (
              <div key={q.id} className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
                <div className="min-w-0">
                  <div className="text-xs font-bold text-slate-900 truncate">"{q.query}"</div>
                  <div className="text-[10px] text-slate-500 font-semibold">{q.city} · {q.category}</div>
                </div>
                <div className="text-[10px] text-slate-400 font-mono">{timeAgo(q.created_at)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, tone }: { icon: typeof Eye; label: string; value: number; tone: string }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-extrabold">{label}</div>
        <Icon className={`h-4 w-4 ${tone}`} />
      </div>
      <div className="text-2xl font-extrabold text-slate-900 mt-2 tabular-nums">{formatNumber(value)}</div>
    </Card>
  );
}

function RankedList({ title, icon, rows }: { title: string; icon: React.ReactNode; rows: { label: string; sub: string; value: string }[] }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-3">{icon}<div className="text-sm font-bold text-slate-900">{title}</div></div>
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl hover:bg-slate-50 px-2 py-1.5 transition">
            <div className="h-6 w-6 rounded-lg bg-slate-100 grid place-items-center text-[11px] font-mono text-slate-500 font-bold">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold text-slate-900 truncate">{r.label}</div>
              <div className="text-[10px] text-slate-500 font-semibold truncate">{r.sub}</div>
            </div>
            <div className="text-xs font-mono font-extrabold text-slate-800 tabular-nums">{r.value}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ComboCard({ title, icon, rows }: { title: string; icon: React.ReactNode; rows: { a: string; b: string; count: number; avg: number }[] }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-3">{icon}<div className="text-sm font-bold text-slate-900">{title}</div></div>
      <div className="space-y-1.5">
        {rows.length === 0 && <div className="text-xs text-slate-400 py-3">No combinations yet.</div>}
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl hover:bg-slate-50 px-2 py-1.5 transition">
            <div className="h-6 w-6 rounded-lg bg-slate-100 grid place-items-center text-[11px] font-mono text-slate-500 font-bold">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold text-slate-900 truncate">
                <span>{r.a}</span> <span className="text-slate-400">·</span> <span className="text-slate-600">{r.b}</span>
              </div>
              <div className="text-[10px] text-slate-500 font-semibold">avg {r.avg.toFixed(1)} results</div>
            </div>
            <div className="text-xs font-mono text-emerald-700 font-extrabold tabular-nums">{r.count}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function BarList({ title, icon, rows }: { title: string; icon: React.ReactNode; rows: [string, number][] }) {
  const max = Math.max(1, ...rows.map((r) => r[1]));
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-3">{icon}<div className="text-sm font-bold text-slate-900">{title}</div></div>
      <div className="space-y-2.5">
        {rows.map(([label, v]) => (
          <div key={label}>
            <div className="flex items-center justify-between text-xs mb-1 font-semibold">
              <span className="text-slate-800 font-bold">{label}</span>
              <span className="text-slate-500 font-mono text-[11px]">{formatNumber(v)}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-emerald-600 rounded-full" style={{ width: `${(v / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
